/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */

import qs from 'qs';
import {GenericObject} from '@ion/core/src/types/entities';

export interface UseUrlResponse {
  getQueryParams: () => GenericObject;
  hasQueryParam: (param: string) => boolean;
  getPath: () => string;
  getOrigin: () => string;
}

/**
 * Hook that defines simple utility to fetch information on the current state of the application url.
 * This hook uses window object hence it is used only if component SSR is false.
 * It cannot be used for server side rendering.
 *
 *   ```typescript
 *   import {useUrl} from '@ion/applications/src/hooks/useUrl'
 *   const {
 *     getPath,
 *     hasQueryParam,
 *     getQueryParams,
 *     getOrigin
 *   } = useUrl();
 *   ```
 */
export function useUrl(): UseUrlResponse {
  /**
   * Function to get the current url origin of the application
   *
   * @returns origin of the page
   *
   *  ```typescript
   *   const currentUrl = getOrigin(); // 'https://admin.calamp.com'
   *  ```
   */
  const getOrigin = (): string => window?.location?.origin;

  /**
   * Function to get the current path of the application
   *
   * @returns path of the page
   *
   *  ```typescript
   *   const path = getPath(); // '/tracking'
   *  ```
   */
  const getPath = (): string => window?.location?.pathname;

  /**
   * Function to check if a query param exists in the current state of the application.
   *
   * @param string query param name
   * @returns boolean
   *
   *  ```typescript
   *    const isParamExist = hasQueryParam('assetDetail'); // false || true
   *  ```
   */
  const hasQueryParam = (param: string): boolean =>
    Object.keys(getQueryParams() || {}).includes(param);

  /**
   * Function to query params as object at the current state of the application.
   *
   * @returns Object
   *
   *  ```typescript
   *    const queryParams = getQueryParams('assetDetail'); // {'env': 'us'}
   *  ```
   */
  const getQueryParams = (): GenericObject => qs.parse(window?.location?.search, {
    ignoreQueryPrefix : true
  });

  return {
    getQueryParams,
    hasQueryParam,
    getPath,
    getOrigin
  };
};

export default useUrl;
